import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, X } from "lucide-react";
import { useContent } from "@/lib/use-content";
import { useJsonLd, breadcrumbJsonLd, faqPageJsonLd } from "@/lib/json-ld";
import { trackCtaClick } from "@/lib/cta-analytics";
import { fotosPlanOnlineVisibles } from "@/lib/plan-online-fotos";

type Plan = { tag: string; price: string; period: string };

const PASOS = [
  ["Evaluación inicial gratuita", "Nos envías videos de los ejercicios que te pedimos y definimos tu nivel de partida."],
  ["Tu planificación", "Armamos una planificación individual de 2 a 4 días por semana, según tus objetivos y tu disponibilidad."],
  ["Entrenas y registras", "Entrenas donde quieras y registras tus sesiones en el Portal. Ese registro es lo que permite ajustar tu plan."],
  ["Revisión semanal", "Revisamos tu semana y ajustamos el plan. La respuesta llega el sábado o el domingo."],
] as const;

const INCLUYE = [
  "Evaluación inicial gratuita por video",
  "Planificación individual de 2 a 4 días por semana",
  "Revisión semanal con ajuste del plan (respuesta el sábado o el domingo)",
  "Corrección por video de la primera y la última serie de cada ejercicio, según tu planificación",
  "Consultas por WhatsApp en horario de atención",
  "Una clase presencial al mes para corregir técnica, en el lugar donde el profesor imparte clases (sujeta a disponibilidad)",
  "Un replanteo mayor de tu plan por mes",
];

const NO_INCLUYE = [
  "Videollamadas (ni iniciales ni de seguimiento)",
  "Asesoría nutricional",
  "Indicaciones médicas: ante una lesión o molestia, consulta a un profesional de la salud",
];

const FAQS = [
  { q: "¿El plan online incluye videollamadas?", a: "No. Funciona con videos grabados por ti, revisión semanal y consultas por WhatsApp en horario de atención." },
  { q: "¿Cómo es la clase presencial del mes?", a: "Es una clase al mes para corregir técnica, en el lugar donde el profesor imparte clases. Depende de la disponibilidad de horarios y se coordina por WhatsApp." },
  { q: "¿Qué pasa si no registro mis sesiones?", a: "El registro de sesiones en el Portal es obligatorio: sin registro, el plan queda sin ajuste esa semana." },
  { q: "¿Puedo pausar el plan?", a: "Las pausas siguen la política general: 1 mes en el plan semestral y 2 en el anual, solo con más de 3 meses de antigüedad." },
];

export function PlanOnlinePage() {
  useJsonLd("ld-breadcrumb-plan-online", breadcrumbJsonLd([{ name: "Inicio", path: "/" }, { name: "Clases", path: "/clases" }, { name: "Plan online", path: "/plan-online" }]));
  useJsonLd("ld-faq-plan-online", faqPageJsonLd(FAQS));
  // El monto sale del mismo contenido que /clases (Panel CP → Clases), para que nunca diga otra cosa.
  const plans = useContent<{ items: Plan[] }>("clases_plans");
  const online = plans.items.find((p) => p.tag === "ONLINE 1:1");
  // Con la lista de fotos vacía (src/lib/plan-online-fotos.ts) la sección entera no se renderiza.
  const fotos = fotosPlanOnlineVisibles();
  const trackAgendar = () => trackCtaClick("cp_agendar_clase", { sede: "plan-online" });

  return (
    <>
      <section className="py-24 md:py-32 border-b border-[var(--cp-line)] relative overflow-hidden">
        <div className="absolute inset-0 cp-grid-bg opacity-30" />
        <div className="cp-container relative">
          <span className="cp-eyebrow">PLAN ONLINE</span>
          <h1 className="mt-6 text-5xl md:text-7xl lg:text-8xl text-white">Entrena <span className="text-[var(--cp-cyan)]">calistenia</span><br/>donde quieras</h1>
          <p className="mt-8 max-w-xl text-lg text-white/70 leading-relaxed">Una planificación hecha para ti, con revisión semanal y una clase presencial al mes (sujeta a disponibilidad).</p>
          {online && (
            <div className="mt-8 flex items-baseline gap-2 flex-wrap">
              <span className="font-display font-bold text-5xl text-[var(--cp-cyan)]">{online.price}</span>
              <span className="font-mono text-xs text-white/60">{online.period}</span>
            </div>
          )}
          <Link to="/contacto" onClick={trackAgendar} className="cp-btn-primary mt-8 inline-flex">Pedir mi evaluación gratuita <ArrowRight size={16}/></Link>
        </div>
      </section>

      <section className="py-24 border-b border-[var(--cp-line)]">
        <div className="cp-container">
          <div className="mb-12">
            <span className="cp-eyebrow">CÓMO FUNCIONA</span>
            <h2 className="mt-6 text-4xl md:text-5xl text-white">Cuatro pasos, cada semana</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PASOS.map(([t, d], i) => (
              <div key={t} className="border border-[var(--cp-line)] bg-black p-8">
                <span className="font-mono text-xs text-[var(--cp-cyan)]">0{i + 1}</span>
                <h3 className="mt-6 font-display text-2xl text-white mb-3">{t}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-b border-[var(--cp-line)] bg-[var(--cp-surface)]/30">
        <div className="cp-container grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-[var(--cp-blue)] bg-black p-8">
            <span className="cp-eyebrow">QUÉ INCLUYE</span>
            <ul className="mt-6 space-y-3">
              {INCLUYE.map((x) => (
                <li key={x} className="flex items-start gap-2 text-sm text-white/80"><Check size={14} className="text-[var(--cp-cyan)] mt-1 shrink-0" /> {x}</li>
              ))}
            </ul>
          </div>
          <div className="border border-[var(--cp-line)] bg-black p-8">
            <span className="cp-eyebrow">QUÉ NO INCLUYE</span>
            <ul className="mt-6 space-y-3">
              {NO_INCLUYE.map((x) => (
                <li key={x} className="flex items-start gap-2 text-sm text-white/70"><X size={14} className="text-white/50 mt-1 shrink-0" /> {x}</li>
              ))}
            </ul>
            <p className="mt-8 text-sm text-white/50 leading-relaxed">Si tienes una lesión o una condición de salud, consulta antes con un profesional de la salud. El plan online es para personas desde los 16 años.</p>
          </div>
        </div>
      </section>

      {fotos.length > 0 && (
        <section className="py-24 border-b border-[var(--cp-line)]">
          <div className="cp-container">
            <div className="mb-12">
              <span className="cp-eyebrow">EN LA PRÁCTICA</span>
              <h2 className="mt-6 text-4xl md:text-5xl text-white">Así se ve el plan online</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {fotos.map((f) => (
                <figure key={f.src} className="border border-[var(--cp-line)] bg-black">
                  <img src={f.src} alt={f.alt} width={f.width} height={f.height} loading="lazy" decoding="async" className="w-full aspect-[4/5] object-cover" />
                  {f.pie && <figcaption className="p-4 text-sm text-white/60">{f.pie}</figcaption>}
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-24 border-b border-[var(--cp-line)]">
        <div className="cp-container max-w-3xl">
          <div className="mb-12">
            <span className="cp-eyebrow">PREGUNTAS FRECUENTES</span>
            <h2 className="mt-6 text-4xl md:text-5xl text-white">Antes de empezar</h2>
          </div>
          <div className="space-y-6">
            {FAQS.map((f) => (
              <div key={f.q} className="border-b border-[var(--cp-line)] pb-6">
                <h3 className="font-display text-xl text-white mb-2">{f.q}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="cp-container text-center max-w-2xl mx-auto">
          <h2 className="font-display text-4xl md:text-5xl text-white">¿Quieres partir con tu evaluación?</h2>
          <Link to="/contacto" onClick={trackAgendar} className="cp-btn-primary mt-8 inline-flex">Escríbenos <ArrowRight size={16}/></Link>
          <p className="mt-6 text-sm text-white/50">¿Prefieres entrenar en grupo? Mira las <Link to="/clases" className="text-[var(--cp-cyan)] hover:underline">clases presenciales</Link>.</p>
        </div>
      </section>
    </>
  );
}
